import { useEffect } from 'react';
import { Form, Input, Select, Button, Space } from 'antd';
import type { FormInstance } from 'antd';
import type { LeadDetail, PersonEditInput } from './types';
import { STATUS_OPTIONS, TEMP_OPTIONS } from './labels';
import { isValidLinkedinUrl, LINKEDIN_URL_MAX_LEN } from './linkedinUrl';
import { personToEditInput } from './mapLeadDetail';

export interface LeadEditFormProps {
  initial: LeadDetail['person'];
  submitting: boolean;
  onSubmit: (fields: PersonEditInput) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * OPTIONAL externally-owned form instance. LeadDetailBody supplies its own
   * so the contact-enrichment panel's "Use this value" can push a single
   * field into an ALREADY-open form via `form.setFieldValue()` without
   * disturbing whatever else is mid-edit. Omit it to get a private instance
   * scoped to this component, as before.
   */
  form?: FormInstance<PersonEditInput>;
}

type Option = { value: string; label: string };

/** Keep a lead's current (possibly non-standard) value selectable so editing
 *  other fields never forces a status/temperature change. */
function withCurrent(options: Option[], current: string): Option[] {
  if (current === '' || options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: current }];
}

export function LeadEditForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
  onDirtyChange,
  form: externalForm,
}: LeadEditFormProps) {
  const [form] = Form.useForm<PersonEditInput>(externalForm);

  const initialValues: PersonEditInput = personToEditInput(initial);

  useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={() => onDirtyChange?.(form.isFieldsTouched())}
      // Trim once at the boundary so the value the backend stores is the same
      // one the card later shows, whether it was typed or prefilled from a
      // sync-written row with surrounding whitespace (the backend trims too).
      onFinish={(values) => onSubmit({ ...values, linkedinUrl: values.linkedinUrl.trim() })}
    >
      <Form.Item label="First name" name="firstName" rules={[{ max: 200 }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Last name" name="lastName" rules={[{ max: 200 }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Display name" name="displayName" rules={[{ max: 200 }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Title" name="title" rules={[{ max: 200 }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Company" name="company" rules={[{ max: 200 }]}>
        <Input />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        rules={[
          {
            validator: (_, v: string) =>
              !v || /.+@.+\..+/.test(v)
                ? Promise.resolve()
                : Promise.reject(new Error('Enter a valid email address')),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Phone" name="phone" rules={[{ max: 64 }]}>
        <Input />
      </Form.Item>
      <Form.Item
        label="LinkedIn URL"
        name="linkedinUrl"
        rules={[
          { max: LINKEDIN_URL_MAX_LEN },
          {
            validator: (_, v: string) =>
              isValidLinkedinUrl(v ?? '')
                ? Promise.resolve()
                : Promise.reject(new Error('Enter a valid URL')),
          },
        ]}
      >
        <Input placeholder="linkedin.com/in/username" />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <Select options={withCurrent(STATUS_OPTIONS, initial.status)} />
      </Form.Item>
      <Form.Item label="Temperature" name="temperature">
        <Select options={withCurrent(TEMP_OPTIONS, initial.temperature)} />
      </Form.Item>
      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save
        </Button>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </Space>
    </Form>
  );
}
